import '../styles/components/help.css';
import '../styles/components/badges.css';
import '../styles/components/cards.css';
import '../styles/components/progress.css';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'modes', label: 'Modes' },
  { id: 'discovery-levels', label: 'Discovery Levels' },
  { id: 'age-levels', label: 'Age Estimation Levels' },
  { id: 'discovery-results', label: 'Discovery Results' },
  { id: 'age-results', label: 'Age Estimation Results' },
  { id: 'job-statuses', label: 'Job Statuses' },
  { id: 'job-badges', label: 'Job Detail Badges' },
  { id: 'advanced', label: 'Advanced Options' },
  { id: 'progress', label: 'Progress & Live Activity' },
  { id: 'downloads', label: 'Downloads' },
  { id: 'crash', label: 'Crash Recovery' },
  { id: 'flowchart', label: 'Discovery Flowchart' },
];

const DownloadIcon = ({ className }) => (
  <span className={`help-dl-icon ${className}`}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  </span>
);

export default function HelpPage() {
  return (
    <div className="help-page page-enter">
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 6, letterSpacing: '-0.6px' }}>
        Guide
      </h2>
      <p style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 20 }}>
        Everything you need to know about using this application.
      </p>

      <div className="help-banner warn" style={{ marginBottom: 28 }}>
        <strong>File names matter.</strong> All processing data is tracked by file name. If you rename a file, the system will treat it as a brand new file — previous progress, results, and resume points will not carry over.
      </div>

      {/* Table of Contents */}
      <div className="help-toc">
        <div className="help-toc-title">Contents</div>
        <ul className="help-toc-list">
          {TOC.map(t => (
            <li key={t.id}><a href={`#${t.id}`}>{t.label}</a></li>
          ))}
        </ul>
      </div>

      {/* 1. Overview */}
      <div className="help-section" id="overview">
        <h3>Overview</h3>
        <p className="help-text">
          This application processes spreadsheets of employee data to either <strong>find their LinkedIn profiles</strong> (Discovery mode) or <strong>estimate their age from photos</strong> (Age Estimation mode).
        </p>
        <p className="help-text">
          You upload a file, choose your settings, and the system processes each row automatically. Progress is saved after every row, so you never lose work.
        </p>
      </div>

      {/* 2. Getting Started */}
      <div className="help-section" id="getting-started">
        <h3>Getting Started</h3>
        <p className="help-text">
          <strong>1.</strong> Go to <strong>New Job</strong> in the sidebar and drop your <strong>.xlsx</strong> or <strong>.csv</strong> file into the upload area.
        </p>
        <p className="help-text">
          <strong>2.</strong> Choose a <strong>mode</strong> — Age Estimation or LinkedIn Discovery.
        </p>
        <p className="help-text">
          <strong>3.</strong> Choose a <strong>level</strong> — higher levels are more accurate but slower and more expensive.
        </p>
        <p className="help-text">
          <strong>4.</strong> Review the cost and time estimates at the bottom, then click <strong>Start Processing</strong>.
        </p>
        <p className="help-text">
          After the file is uploaded, you will see a summary of your file. The summary changes depending on the mode:
        </p>

        <div className="help-example">
          <div className="help-example-label">Discovery Mode</div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            <span>32,614 total rows</span>
            <span style={{ margin: '0 8px', color: 'var(--text3)' }}>|</span>
            <span>6,182 without LinkedIn</span>
            <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 4 }}>(26,432 already have LinkedIn)</span>
            <span style={{ margin: '0 8px', color: 'var(--text3)' }}>|</span>
            <span>1 already processed</span>
            <span style={{ margin: '0 8px', color: 'var(--text3)' }}>|</span>
            <span style={{ fontWeight: 700, color: 'var(--accent)' }}>6,181 remaining</span>
          </div>
        </div>
        <p className="help-text">
          <strong>Total rows</strong> is everything in your file. <strong>Without LinkedIn</strong> shows how many rows do not have a LinkedIn URL yet — only these rows will be processed. <strong>Already processed</strong> is how many were completed in a previous run. <strong>Remaining</strong> is how many will actually be processed this time.
        </p>

        <div className="help-example">
          <div className="help-example-label">Age Estimation Mode</div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            <span>32,614 total rows</span>
            <span style={{ margin: '0 8px', color: 'var(--text3)' }}>|</span>
            <span>500 already processed</span>
            <span style={{ margin: '0 8px', color: 'var(--text3)' }}>|</span>
            <span style={{ fontWeight: 700, color: 'var(--accent)' }}>32,114 remaining</span>
          </div>
        </div>
        <p className="help-text">
          In Age Estimation mode, all rows are eligible — there is no LinkedIn filter. The summary shows how many rows were already processed and how many remain.
        </p>
      </div>

      {/* 3. Modes */}
      <div className="help-section" id="modes">
        <h3>Modes</h3>
        <div className="help-item">
          <div className="help-item-badge"><span className="info-badge badge-mode">Age Estimation</span></div>
          <span className="help-item-text">
            Finds employee photos on company websites (and optionally LinkedIn), then uses facial recognition to estimate their age.
          </span>
        </div>
        <div className="help-item">
          <div className="help-item-badge"><span className="info-badge badge-mode">Discovery</span></div>
          <span className="help-item-text">
            Finds LinkedIn profile URLs for people using their name, company, department, city, and province. Does not estimate age.
          </span>
        </div>
      </div>

      {/* 4. Discovery Levels */}
      <div className="help-section" id="discovery-levels">
        <h3>Discovery Levels</h3>
        <p className="help-text">
          Higher levels use more techniques to find the right person. Each level includes everything from the levels below it.
        </p>

        <div className="help-level-row">
          <div className="help-level-card">
            <div className="level-tag l1">LEVEL 1</div>
            <div className="level-name">Search</div>
            <div className="level-pipeline">Website &rarr; Photo &rarr; Name + Image search</div>
            <div className="level-meta">
              <div className="level-meta-row"><span className="level-meta-label">Cost</span><span className="level-meta-value">~$0.015/row</span></div>
              <div className="level-meta-row"><span className="level-meta-label">Speed</span><span className="level-meta-value">~5-20s</span></div>
            </div>
          </div>
          <div className="help-level-text">
            <strong>Cheapest and fastest.</strong> Searches the <strong>company website</strong> for a LinkedIn link and uses <strong>Google text and image search</strong> by name, company, and photo. Works well for most names.
          </div>
        </div>

        <div className="help-level-row">
          <div className="help-level-card">
            <div className="level-tag l2">LEVEL 2</div>
            <div className="level-name">Result Verification</div>
            <div className="level-pipeline">Level 1 + Photo comparison</div>
            <div className="level-meta">
              <div className="level-meta-row"><span className="level-meta-label">Cost</span><span className="level-meta-value">~$0.025/row</span></div>
              <div className="level-meta-row"><span className="level-meta-label">Speed</span><span className="level-meta-value">~15-35s</span></div>
            </div>
          </div>
          <div className="help-level-text">
            Opens the <strong>top search result</strong> on LinkedIn and <strong>compares the profile photo</strong> against the company website photo. If the photo comparison is a strong match (90%+ similarity), the result is upgraded to <strong>confirmed</strong>.
          </div>
        </div>

      </div>

      {/* 5. Age Estimation Levels */}
      <div className="help-section" id="age-levels">
        <h3>Age Estimation Levels</h3>

        <div className="help-level-row">
          <div className="help-level-card">
            <div className="level-tag l1">LEVEL 1</div>
            <div className="level-name">Website Only</div>
            <div className="level-pipeline">Website &rarr; Age Detection</div>
            <div className="level-meta">
              <div className="level-meta-row"><span className="level-meta-label">Cost</span><span className="level-meta-value">~$0.005/row</span></div>
              <div className="level-meta-row"><span className="level-meta-label">Speed</span><span className="level-meta-value">~3-10s</span></div>
            </div>
          </div>
          <div className="help-level-text">
            Finds a <strong>headshot</strong> on the <strong>company website</strong> and uses <strong>facial recognition</strong> to estimate age. <strong>Fast and cheap</strong>, but only has one photo to work with.
          </div>
        </div>

        <div className="help-level-row">
          <div className="help-level-card">
            <div className="level-tag l2">LEVEL 2</div>
            <div className="level-name">Full Estimation</div>
            <div className="level-pipeline">Website &rarr; LinkedIn &rarr; Age Detection</div>
            <div className="level-meta">
              <div className="level-meta-row"><span className="level-meta-label">Cost</span><span className="level-meta-value">~$0.010/row</span></div>
              <div className="level-meta-row"><span className="level-meta-label">Speed</span><span className="level-meta-value">~10-25s</span></div>
            </div>
          </div>
          <div className="help-level-text">
            Uses photos from both the <strong>company website</strong> and <strong>LinkedIn</strong> for a more reliable estimate. <strong>Recommended</strong> when accuracy is important.
          </div>
        </div>
      </div>

      {/* 6. Discovery Results */}
      <div className="help-section" id="discovery-results">
        <h3>Discovery Results</h3>
        <p className="help-text">After processing, each row gets one of these statuses:</p>

        <div className="help-item">
          <div className="help-item-badge">
            <span className="status-badge completed">confirmed</span>
          </div>
          <span className="help-item-text">
            <strong>High-confidence match.</strong> The LinkedIn URL was found directly on the company website, or the photo comparison was a strong match (90%+ similarity).
          </span>
        </div>

        <div className="help-item">
          <div className="help-item-badge">
            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: 'rgba(234, 179, 8, 0.12)', color: 'var(--yellow)' }}>probable</span>
          </div>
          <span className="help-item-text">
            <strong>Likely correct, but not 100% certain.</strong> Good text match with some photo or AI confirmation. Worth spot-checking a few to verify quality.
          </span>
        </div>

        <div className="help-item">
          <div className="help-item-badge">
            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: 'rgba(59, 130, 246, 0.12)', color: 'var(--accent)' }}>ambiguous</span>
          </div>
          <span className="help-item-text">
            <strong>Multiple possible matches, none clearly the right one.</strong> Consider re-running these rows at a higher level for better accuracy.
          </span>
        </div>

        <div className="help-item">
          <div className="help-item-badge">
            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: 'var(--surface3)', color: 'var(--text3)' }}>not_found</span>
          </div>
          <span className="help-item-text">
            <strong>No LinkedIn profile found.</strong> The person may not have a LinkedIn account, or their name/company combination did not return any results.
          </span>
        </div>

        <div className="help-item">
          <div className="help-item-badge">
            <span className="status-badge failed">error</span>
          </div>
          <span className="help-item-text">
            <strong>Something went wrong.</strong> The row failed to process due to a technical issue. It will be retried automatically in the next run.
          </span>
        </div>
      </div>

      {/* 7. Age Estimation Results */}
      <div className="help-section" id="age-results">
        <h3>Age Estimation Results</h3>

        <div className="help-item">
          <div className="help-item-badge">
            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: 'rgba(34, 197, 94, 0.12)', color: 'var(--green)' }}>both_images</span>
          </div>
          <span className="help-item-text">
            Age estimated using photos from <strong>both</strong> the company website and LinkedIn. Most reliable result.
          </span>
        </div>

        <div className="help-item">
          <div className="help-item-badge">
            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: 'rgba(59, 130, 246, 0.12)', color: 'var(--accent)' }}>website_only</span>
          </div>
          <span className="help-item-text">
            Age estimated from the company <strong>website photo only</strong>. No LinkedIn photo was available or used.
          </span>
        </div>

        <div className="help-item">
          <div className="help-item-badge">
            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: 'rgba(59, 130, 246, 0.12)', color: 'var(--accent)' }}>linkedin_only</span>
          </div>
          <span className="help-item-text">
            Age estimated from the <strong>LinkedIn photo only</strong>. No website photo was found.
          </span>
        </div>

        <div className="help-item">
          <div className="help-item-badge">
            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: 'var(--surface3)', color: 'var(--text3)' }}>not_found</span>
          </div>
          <span className="help-item-text">
            <strong>No usable photo found</strong> on either the website or LinkedIn. Age could not be estimated.
          </span>
        </div>

        <div className="help-item">
          <div className="help-item-badge">
            <span className="status-badge failed">failed</span>
          </div>
          <span className="help-item-text">
            Processing error. The row will be retried automatically in the next run.
          </span>
        </div>
      </div>

      {/* 8. Job Statuses */}
      <div className="help-section" id="job-statuses">
        <h3>Job Statuses</h3>
        <p className="help-text">Every job goes through these stages:</p>

        <div className="help-item">
          <div className="help-item-badge"><span className="status-badge pending">pending</span></div>
          <span className="help-item-text">Waiting in the queue. The system processes one job at a time — your job will start when the current one finishes.</span>
        </div>
        <div className="help-item">
          <div className="help-item-badge"><span className="status-badge running">running</span></div>
          <span className="help-item-text">Currently being processed. You can watch progress in real time on the job detail page.</span>
        </div>
        <div className="help-item">
          <div className="help-item-badge"><span className="status-badge completed">completed</span></div>
          <span className="help-item-text">Finished successfully. You can download the results.</span>
        </div>
        <div className="help-item">
          <div className="help-item-badge"><span className="status-badge failed">failed</span></div>
          <span className="help-item-text">Hit an error and stopped. Check the error message on the job detail page.</span>
        </div>
      </div>

      {/* 9. Job Detail Badges */}
      <div className="help-section" id="job-badges">
        <h3>Job Detail Badges</h3>
        <p className="help-text">When you open a job, you will see colored badges at the top showing the job's settings:</p>

        <div className="help-example">
          <div className="help-badge-row">
            <span className="info-badge badge-mode">Discovery</span>
            <span className="help-badge-desc">The mode that was selected</span>
          </div>
          <div className="help-badge-row">
            <span className="info-badge badge-level">Level 2</span>
            <span className="help-badge-desc">Which level of processing</span>
          </div>
          <div className="help-badge-row">
            <span className="info-badge badge-range">Start #100 / Limit 50</span>
            <span className="help-badge-desc">Row range — if you set a start row or limit</span>
          </div>
          <div className="help-badge-row">
            <span className="info-badge badge-engine">Apify</span>
            <span className="help-badge-desc">Which LinkedIn scraping service is being used</span>
          </div>
          <div className="help-badge-row">
            <span className="info-badge badge-override">image search</span>
            <span className="help-badge-desc">Settings you changed from the level's defaults (orange = custom override)</span>
          </div>
          <div className="help-badge-row">
            <span className="info-badge badge-time">Jan 23 14:30</span>
            <span className="help-badge-desc">When the job was created</span>
          </div>
        </div>
      </div>

      {/* 10. Advanced Options */}
      <div className="help-section" id="advanced">
        <h3>Advanced Options</h3>
        <p className="help-text">
          Click <strong>Advanced Options</strong> on the New Job page to see these settings. You usually do not need to change them — the level you choose sets sensible defaults.
        </p>

        <p className="help-text"><strong>LinkedIn Engine</strong> — Which service fetches LinkedIn profile data. Apify is the default and most reliable. Only used when the level requires opening LinkedIn profiles.</p>

        <p className="help-text"><strong>Start Row</strong> — Skip ahead to a specific row number. Useful if you want to resume from a particular point. Leave blank to start automatically from where you left off.</p>

        <p className="help-text"><strong>Row Limit</strong> — Only process this many rows. Useful for testing — set it to 10 or 20 to check results before running the full file.</p>

        <div className="help-example">
          <div className="help-example-label">General</div>
          <p className="help-text" style={{ marginBottom: 8 }}>
            <strong>Start fresh</strong> — Deletes all previous processing data for this file and starts over from row 1. Previous jobs for this file will appear faded (superseded) in Job History, but they are still visible and their downloads still work. Use this when you want to reprocess everything with different settings. If you do not check this, the system automatically skips rows that were already completed and only processes the remaining ones.
          </p>
        </div>

        <div className="help-example" style={{ marginTop: 16 }}>
          <div className="help-example-label">Discovery Options</div>
          <p className="help-text" style={{ marginBottom: 0 }}><strong>Verify top result (photo comparison)</strong> — Opens the best search result on LinkedIn and compares photos. Included in Level 2.</p>
        </div>

        <div className="help-example" style={{ marginTop: 16 }}>
          <div className="help-example-label">Age Estimation Options</div>
          <p className="help-text" style={{ marginBottom: 4 }}><strong>Skip LinkedIn scraping</strong> — Only use the company website photo. Checked by default in Level 1.</p>
          <p className="help-text" style={{ marginBottom: 0 }}><strong>Skip website scraping</strong> — Only use LinkedIn photos. Rarely needed.</p>
        </div>
      </div>

      {/* 11. Progress & Live Activity */}
      <div className="help-section" id="progress">
        <h3>Progress & Live Activity</h3>

        <p className="help-text">
          The <strong>progress bar</strong> shows how many rows have been processed. The percentage updates every 2 seconds.
        </p>

        <div className="help-example">
          <div className="progress-bar-header">
            <span>247 / 1,000 rows processed <span style={{ color: 'var(--text3)' }}>— John Smith #248</span></span>
            <span className="progress-bar-pct">24.7%</span>
          </div>
          <div className="progress-bar-track">
            <div className="progress-bar-fill" style={{ width: '24.7%' }} />
          </div>
        </div>

        <p className="help-text">
          The <strong>live activity log</strong> shows what the system is doing for each person in real time. Different colors indicate different outcomes:
        </p>

        <div className="help-example help-log-example">
          <div className="live-log-header">
            <span>Live Activity</span>
            <div className="live-dot" />
          </div>
          <div style={{ borderTop: '1px solid var(--border)' }}>
            <div className="log-row log-row-start">
              <span className="log-idx">#248</span>
              <span className="log-name" style={{ color: 'var(--accent)' }}>John Smith</span>
              <span className="log-msg msg-start">Processing...</span>
              <span className="log-level info">info</span>
            </div>
            <div className="log-row">
              <span className="log-idx">#248</span>
              <span className="log-name">John Smith</span>
              <span className="log-msg msg-info">Scraping company website</span>
              <span className="log-level info">info</span>
            </div>
            <div className="log-row">
              <span className="log-idx">#248</span>
              <span className="log-name">John Smith</span>
              <span className="log-msg msg-success">LinkedIn URL found on website</span>
              <span className="log-level success">success</span>
            </div>
            <div className="log-row log-row-end">
              <span className="log-idx">#248</span>
              <span className="log-name">John Smith</span>
              <span className="log-msg msg-success">Result: CONFIRMED via website</span>
              <span className="log-level success">success</span>
            </div>
            <div className="log-row log-row-start">
              <span className="log-idx">#249</span>
              <span className="log-name" style={{ color: 'var(--accent)' }}>Jane Doe</span>
              <span className="log-msg msg-start">Processing...</span>
              <span className="log-level info">info</span>
            </div>
            <div className="log-row">
              <span className="log-idx">#249</span>
              <span className="log-name">Jane Doe</span>
              <span className="log-msg msg-warn">Result: PROBABLE via search only</span>
              <span className="log-level warn">warn</span>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <p className="help-text" style={{ marginBottom: 6 }}><span style={{ color: 'var(--green)', fontWeight: 700 }}>Green text</span> = successful match or confirmed result</p>
          <p className="help-text" style={{ marginBottom: 6 }}><span style={{ color: 'var(--yellow)', fontWeight: 700 }}>Yellow text</span> = partial match or needs attention</p>
          <p className="help-text" style={{ marginBottom: 6 }}><span style={{ color: 'var(--red)', fontWeight: 700 }}>Red text</span> = error or not found</p>
          <p className="help-text" style={{ marginBottom: 6 }}><span style={{ color: 'var(--accent)', fontWeight: 600 }}>Blue name</span> = currently processing this person</p>
        </div>
      </div>

      {/* 12. Downloads */}
      <div className="help-section" id="downloads">
        <h3>Downloads</h3>
        <p className="help-text">
          Once a job is completed, you can download the results as an enriched spreadsheet. On the <strong>Job History</strong> page, you will see download icons next to completed jobs:
        </p>

        <div className="help-dl-row">
          <DownloadIcon className="latest" />
          <span className="help-item-text">
            <strong>Blue download</strong> — the latest results for this file. This is the one you want.
          </span>
        </div>

        <div className="help-dl-row">
          <DownloadIcon className="old" />
          <span className="help-item-text">
            <strong>Gray download</strong> — results from an older run that was replaced by a newer one.
          </span>
        </div>

        <p className="help-text" style={{ marginTop: 12 }}>
          Rows that appear <strong>faded</strong> (low opacity) are <strong>superseded</strong> — their results were replaced when you ran the same file again with "Fresh" enabled:
        </p>

        <div className="help-superseded-demo">
          contacts.xlsx &nbsp;&nbsp; Discovery &nbsp;&nbsp; Level 1 &nbsp;&nbsp; 500 rows &nbsp;&nbsp; <span className="status-badge completed">completed</span> &nbsp;&nbsp; This row is faded because a newer run replaced it.
        </div>
      </div>

      {/* 13. Crash Recovery */}
      <div className="help-section" id="crash">
        <h3>What Happens If the Server Stops?</h3>
        <p className="help-text">
          <strong>Your progress is always safe.</strong> Every row is saved to the database immediately after it finishes processing. If the server stops for any reason, it picks up exactly where it left off when it restarts. You do not need to do anything.
        </p>
        <p className="help-text">You may see one of these warnings:</p>

        <div className="help-banner warn">
          Connection lost — retrying. Job will resume automatically when the server is back.
        </div>
        <p className="help-text" style={{ marginBottom: 8 }}>The page cannot reach the server. This usually means the server restarted or your network dropped. The page keeps retrying — the banner disappears when the connection is restored.</p>

      </div>

      {/* 14. Discovery Flowchart */}
      <div className="help-section" id="flowchart">
        <h3>Discovery Flowchart</h3>
        <p className="help-text">
          Full pipeline showing how discovery processes each row — from website scraping through search, image matching, verification, and final scoring.
        </p>
        <div style={{ marginTop: 16, overflowX: 'auto', cursor: 'pointer' }}>
          <a href="/discovery_flowchart.png" target="_blank" rel="noopener noreferrer">
            <img
              src="/discovery_flowchart.png"
              alt="Discovery pipeline flowchart"
              style={{ width: '100%', borderRadius: 8, border: '1px solid var(--border)' }}
            />
          </a>
          <p className="help-text" style={{ marginTop: 8, fontSize: 12, color: 'var(--text3)' }}>
            Click to open full size in a new tab.
          </p>
        </div>
      </div>

      <div style={{ height: 60 }} />
    </div>
  );
}
